import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenantCode, withTenant } from '../tenant';
import { CloseOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';

function formatDisplayDate(dateStr) {
  if (!dateStr || dateStr.length !== 10) return dateStr;
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function LogPage() {
  const navigate = useNavigate();
  const tenantCode = getTenantCode();
  const [loading, setLoading] = React.useState(false);
  const [loadingDates, setLoadingDates] = React.useState(true);
  const [dates, setDates] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(todayStr());
  const [logContent, setLogContent] = React.useState('');
  const [isEmpty, setIsEmpty] = React.useState(true);
  const [error, setError] = React.useState('');
  const [tenantName, setTenantName] = React.useState('');
  const [tenantLogo, setTenantLogo] = React.useState('');

  const loadDates = React.useCallback(async () => {
    if (!tenantCode) return;
    setLoadingDates(true);
    setError('');
    try {
      const res = await fetch(withTenant('/api/logs/dates'), { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.error || 'Impossibile caricare le date.');
      }
      const data = await res.json();
      const list = Array.isArray(data.dates) ? data.dates : [];
      setDates(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingDates(false);
    }
  }, [tenantCode]);

  const loadLog = React.useCallback(async (date) => {
    if (!tenantCode) return;
    setLoading(true);
    setError('');
    try {
      const url = date ? withTenant(`/api/logs?date=${encodeURIComponent(date)}`) : withTenant('/api/logs');
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.error || 'Impossibile caricare i log.');
      }
      const data = await res.json();
      setLogContent(data.content || '');
      setIsEmpty(data.empty === true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [tenantCode]);

  const loadTenantInfo = React.useCallback(async () => {
    if (!tenantCode) return;
    try {
      const res = await fetch('/api/admin/tenants', { method: 'GET' });
      if (!res.ok) return;
      const tenants = await res.json();
      const match = Array.isArray(tenants)
        ? tenants.find((t) => t.id === tenantCode)
        : null;
      if (match) {
        setTenantName(match.displayName || match.name || '');
        setTenantLogo(match.logoBase64 || match.logoUrl || '');
      }
    } catch {
      // ignore
    }
  }, [tenantCode]);

  React.useEffect(() => {
    loadTenantInfo();
  }, [loadTenantInfo]);

  React.useEffect(() => {
    loadDates();
  }, [loadDates]);

  React.useEffect(() => {
    if (!tenantCode) return;
    loadLog(selectedDate);
  }, [tenantCode, selectedDate, loadLog]);

  const handleSelectDate = (dateStr) => {
    setSelectedDate(dateStr);
  };

  const handleRefresh = () => {
    loadDates();
    loadLog(selectedDate);
  };

  const handleClearLog = async () => {
    if (!tenantCode) return;
    const ok = window.confirm(
      `Vuoi eliminare il log del ${formatDisplayDate(selectedDate)}?\n\nQuesta operazione è irreversibile.`
    );
    if (!ok) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(withTenant(`/api/logs?date=${encodeURIComponent(selectedDate)}`), { method: 'DELETE' });
      if (!res.ok) {
        const problem = await res.json().catch(() => ({}));
        throw new Error(problem.error || 'Impossibile eliminare il log.');
      }
      setLogContent('');
      setIsEmpty(true);
      await loadDates();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const today = todayStr();
  const options = [today, ...dates.filter((d) => d !== today)].map((d) => ({
    value: d,
    label: d === today ? `Oggi – ${formatDisplayDate(d)}` : formatDisplayDate(d),
  }));

  if (!tenantCode) {
    return (
      <div className="wizard-shell">
        <div className="wizard-card">
          <header className="wizard-header">
            <div>
              <h1>Log attività</h1>
              <p className="muted">Nessun tenant selezionato.</p>
            </div>
          </header>
          <div className="error-banner">Tenant non specificato. Aggiungi ?cod=GUID all'URL.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="log-shell">
      <div className="log-card">
        <header className="log-header">
          <div className="log-brand">
            {tenantLogo ? (
              <img src={tenantLogo} alt={tenantName || 'Logo tenant'} className="log-logo" />
            ) : (
              <div className="log-logo-fallback">L</div>
            )}
            <div className="log-brand-text">
              <h1>Log attività</h1>
              <p className="muted">
                Tenant: <span className="mono">{tenantName || tenantCode}</span>
              </p>
            </div>
          </div>
          <div className="log-actions">
            <button className="secondary-button" onClick={handleCancel} disabled={loading}>
              <CloseOutlined /> Annulla
            </button>
            <button className="secondary-button" onClick={handleRefresh} disabled={loading || loadingDates}>
              <SyncOutlined /> Aggiorna
            </button>
            <button className="danger-button" onClick={handleClearLog} disabled={loading || isEmpty}>
              <DeleteOutlined /> Elimina questo log
            </button>
          </div>
        </header>

        {error && <div className="error-banner">{error}</div>}
        {loadingDates && <div className="loading-inline">Caricamento date...</div>}

        <div className="log-body">
          <aside className="log-sidebar">
            <label className="log-sidebar-label" htmlFor="log-date-select">
              Data
            </label>
            <select
              id="log-date-select"
              className="log-date-select"
              value={selectedDate}
              onChange={(e) => handleSelectDate(e.target.value)}
              disabled={loading}
            >
              {options.length === 0 && (
                <option value={today}>Oggi – {formatDisplayDate(today)}</option>
              )}
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </aside>
          <div className="log-content">
            {loading && <div className="loading-inline">Caricamento...</div>}
            {!loading && isEmpty && (
              <div className="log-empty">
                <span className="muted">Nessun log per questa data.</span>
              </div>
            )}
            {!loading && !isEmpty && <pre className="log-text">{logContent}</pre>}
          </div>
        </div>
      </div>
    </div>
  );
}
